"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime";
import { useNavigate } from "@tanstack/react-router";
import { slugifyProjectName } from "@t3tools/shared/projectSlug";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { readEnvironmentApi } from "../environmentApi";
import { useSavedEnvironmentRegistryStore } from "../environments/runtime";
import { usePrimaryEnvironmentId } from "../environments/primary";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { useSettings } from "../hooks/useSettings";
import { findProjectByPath } from "../lib/projectPaths";
import { allocateUniqueSafeProjectSlug, joinSafeProjectPath } from "../lib/safeProjectsRoot";
import { getLatestThreadForProject } from "../lib/threadSort";
import { newCommandId, newProjectId } from "../lib/utils";
import { useServerCwd } from "../rpc/serverState";
import { useSafeAddProjectStore } from "../safeAddProjectStore";
import {
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
  useStore,
} from "../store";
import { buildThreadRouteParams } from "../threadRoutes";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { stackedThreadToast, toastManager } from "./ui/toast";

export function AddSafeProjectDialog() {
  const open = useSafeAddProjectStore((state) => state.open);
  const setOpen = useSafeAddProjectStore((state) => state.setOpen);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPopup className="max-w-md" showCloseButton={false}>
        {open ? <AddSafeProjectDialogBody onClose={() => setOpen(false)} /> : null}
      </DialogPopup>
    </Dialog>
  );
}

function AddSafeProjectDialogBody({ onClose }: { readonly onClose: () => void }) {
  const { t } = useLingui();
  const settings = useSettings();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const savedEnvironmentRegistry = useSavedEnvironmentRegistryStore((state) => state.byId);
  const projectsRoot = useServerCwd();
  const projects = useStore(useShallow(selectProjectsAcrossEnvironments));
  const threads = useStore(useShallow(selectSidebarThreadsAcrossEnvironments));
  const { handleNewThread } = useHandleNewThread();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const environmentId =
    primaryEnvironmentId ?? Object.values(savedEnvironmentRegistry)[0]?.environmentId ?? null;

  const trimmedName = name.trim();
  const canSubmit =
    trimmedName.length > 0 && environmentId !== null && projectsRoot !== null && !submitting;

  async function submit(): Promise<void> {
    if (!environmentId || !projectsRoot || !canSubmit) return;
    const api = readEnvironmentApi(environmentId);
    if (!api) return;

    const baseSlug = slugifyProjectName(trimmedName);
    const environmentProjects = projects.filter(
      (project) => project.environmentId === environmentId,
    );
    const uniqueSlug = allocateUniqueSafeProjectSlug(baseSlug, projectsRoot, environmentProjects);
    const workspaceRoot = joinSafeProjectPath(projectsRoot, uniqueSlug);

    setSubmitting(true);
    try {
      const existing = findProjectByPath(environmentProjects, workspaceRoot);
      if (existing) {
        const latestThread = getLatestThreadForProject(
          threads.filter((thread) => thread.environmentId === existing.environmentId),
          existing.id,
          settings.sidebarThreadSortOrder,
        );
        if (latestThread) {
          await navigate({
            to: "/$environmentId/$threadId",
            params: buildThreadRouteParams(
              scopeThreadRef(latestThread.environmentId, latestThread.id),
            ),
          });
        } else {
          await handleNewThread(scopeProjectRef(existing.environmentId, existing.id)).catch(
            () => undefined,
          );
        }
        onClose();
        return;
      }

      const projectId = newProjectId();
      await api.orchestration.dispatchCommand({
        type: "project.create",
        commandId: newCommandId(),
        projectId,
        title: trimmedName,
        workspaceRoot,
        createWorkspaceRootIfMissing: true,
      });
      await handleNewThread(scopeProjectRef(environmentId, projectId)).catch(() => undefined);
      onClose();
    } catch (error) {
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: t`Failed to add project`,
          description: error instanceof Error ? error.message : t`An error occurred.`,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <DialogHeader>
        <DialogTitle>
          <Trans>New project</Trans>
        </DialogTitle>
      </DialogHeader>
      <div className="px-6 pb-2">
        <Input
          ref={inputRef}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t`Project name`}
          aria-label={t`Project name`}
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <DialogFooter>
        <DialogClose render={<Button type="button" variant="outline" disabled={submitting} />}>
          <Trans>Cancel</Trans>
        </DialogClose>
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? <Trans>Adding…</Trans> : <Trans>Add project</Trans>}
        </Button>
      </DialogFooter>
    </form>
  );
}
